import { Container, Row, Col, Card, CardBody, CardTitle, Button, CardHeader, Nav, NavItem, NavLink, TabContent, TabPane } from "reactstrap";
import Breadcrumbs from "../../../CommonElements/Breadcrumbs/Breadcrumbs";
import { Link, useNavigate, useParams, useLocation } from 'react-router-dom';
import webservice from '../../../Service/webservice';
import { AesEncrypt, AesDecrypt, keyPromise } from "../../../Service/crypto";
import React, { useEffect, useState, useCallback } from "react";
import { Edit, XCircle } from "react-feather";
import Swal from "sweetalert2";
import JSZip from 'jszip';
import api from '../../../api';
import websocket_webservice from '../../../Service/websocket_webservice';
import DesignerTemplateView from './DesignerTemplateView';

// import { Edit } from "react-feather";
interface TemplateItem {
  template_data_get_heading?: Record<string, any>;
}

// Exact field-name fragments that belong in Patient Details
const PATIENT_DETAILS_FIELDS = [
  'patientname', 'patientdob', 'ssnormemberid', 'memberid', 'member_id',
  'coverageyear', 'effectivedate', 'fiscalyear', 'claimpayerid',
  'claimaddress', 'relationship',
];

const OTHER_SECTION_KEYWORDS: Record<string, string[]> = {
  'Subscriber': ['subscriber'],
  'Plan Details': ['insurance', 'plan', 'group', 'payer', 'carrier', 'network', 'feeschedule', 'fee'],
  'Eligibility': ['eligibility', 'eligible', 'coverageeffective', 'coveragestart', 'coverageend', 'planstatus', 'planactive', 'verificationstatus', 'portalstatus'],
  'Deductible & Maximums': ['deductible', 'maximum', 'remaining', 'annual', 'lifetime', 'coinsurance', 'outofpocket'],
  'Benefits & Coverage': ['benefit', 'preventive', 'preventative', 'basic', 'major', 'ortho', 'orthodontic', 'pediatric', 'percent', 'endo', 'perio', 'oral', 'implant', 'crown', 'prosth'],
  'Frequency Limits': ['frequency', 'pervisit', 'peryear', 'bitewing', 'xray', 'exam', 'cleaning', 'prophylaxis'],
  'Restrictions': ['restriction', 'waiting', 'exclusion', 'missingtooth', 'missing'],
  'Claims & Authorization': ['auth', 'preauth', 'claimtype', 'claimform', 'npi', 'taxid'],
  'Notes & History': ['note', 'history', 'comment', 'remark', 'fiscal'],
};

const getSectionForKey = (key: string): string => {
  const lower = key.toLowerCase().replace(/[^a-z0-9]/g, '');

  // Patient Details — only the 9 explicit fields
  if (PATIENT_DETAILS_FIELDS.some(f => lower.includes(f))) return 'Patient Details';

  // All other sections
  for (const [section, keywords] of Object.entries(OTHER_SECTION_KEYWORDS)) {
    if (keywords.some(kw => lower.includes(kw))) return section;
  }

  return 'General Information';
};

const formatLabel = (key: string) =>
  key.replace(/([A-Z])/g, ' $1').replace(/_/g, ' ')
    .replace(/\bSsn\b/i, 'SSN').replace(/\b\w/g, l => l.toUpperCase()).trim();

const Template = ({ template_data_get_heading, id, decryptedTemplateData }: { template_data_get_heading: any, id: any, toggle: () => void, isOpen: boolean, decryptedTemplateData: any[] }) => {
  const [editableData, setEditableData] = useState(() => ({ ...template_data_get_heading }));
  const [isEditMode, setIsEditMode] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setEditableData({ ...template_data_get_heading });
  }, [template_data_get_heading]);

  const handleChange = (key: string, newValue: string) => {
    setEditableData((prevData: any) => ({ ...prevData, [key]: newValue }));
  };

  const handleCancel = () => {
    setEditableData({ ...template_data_get_heading });
    setIsEditMode(false);
  };

  const handleSave = async () => {
    setLoading(true);
    const filteredEditableData = Object.fromEntries(
      Object.entries(editableData).filter(([key]) => key !== null && key !== undefined && key !== '')
    );
    const auditLogData = Object.keys(filteredEditableData).reduce((changed: { field: string; before: any; after: any }[], key) => {
      const before = (template_data_get_heading || {})[key];
      const after = (filteredEditableData as any)[key];
      if (JSON.stringify(before) !== JSON.stringify(after)) {
        changed.push({ field: key, before, after });
      }
      return changed;
    }, []);
    const data = { id, filteredEditableData };
    const Username = localStorage.getItem('UserName') || '';
    const Userid = localStorage.getItem('id') || '';
    const ParentUserId = localStorage.getItem('ParentUserId') || '';
    const auditData = { auditLogData, Username, Refid: id, Userid, ParentUserId };
    try {
      const key = await keyPromise;
      const EncParams = await AesEncrypt(key, data);
      const response = await api.fetch("api/update_template_data", { method: "POST", body: JSON.stringify({ EncParams }) });
      const responseData = await response.json();
      if (responseData === "Updated Successfully") {
        if (auditLogData.length > 0) {
          await api.fetch("api/update_template_data_log", { method: "POST", body: JSON.stringify({ auditData }) });
        }
        Swal.fire({ icon: "success", title: "Insurance Verification Platform", text: "Edited Successfully", customClass: { confirmButton: "btn btn-success" }, allowOutsideClick: false });
      }
      setLoading(false);
      setIsEditMode(false);
    } catch (error) {
      console.error("Error saving:", error);
      setLoading(false);
    }
  };

  const normalizeKey = (s: string) => s.toLowerCase().replace(/[^a-z0-9]/g, '');

  // Build sectionedData ordered by decryptedTemplateData sections
  const sectionedData: Record<string, [string, any][]> = {};
  const sections: string[] = [];

  if (decryptedTemplateData && decryptedTemplateData.length > 0) {
    const matchedKeys = new Set<string>();
    decryptedTemplateData.forEach(([sectionName, templateFields]: [string, any[]]) => {
      const sectionFields: [string, any][] = [];
      (templateFields || []).forEach((tf: any) => {
        const normalizedFieldName = normalizeKey(tf.field_name || '');
        const match = Object.entries(editableData).find(([k]) => k && !matchedKeys.has(k) && normalizeKey(k) === normalizedFieldName);
        if (match) {
          sectionFields.push(match as [string, any]);
          matchedKeys.add(match[0]);
        }
      });
      if (sectionFields.length > 0) {
        sectionedData[sectionName] = sectionFields;
        sections.push(sectionName);
      }
    });
    // Any unmatched fields go to General Information
    const remaining = Object.entries(editableData).filter(([k]) => k && k !== '' && !matchedKeys.has(k));
    if (remaining.length > 0) {
      sectionedData['General Information'] = remaining as [string, any][];
      sections.push('General Information');
    }
  } else {
    // Fallback: keyword-based grouping
    Object.entries(editableData).filter(([k]) => k && k !== '').forEach(([k, v]) => {
      const sec = getSectionForKey(k);
      if (!sectionedData[sec]) { sectionedData[sec] = []; sections.push(sec); }
      sectionedData[sec].push([k, v]);
    });
  }

  // Extract top-bar fields
  const getField = (...keys: string[]) => {
    for (const k of keys) {
      const entry = Object.entries(editableData).find(([key]) => key.toLowerCase().replace(/[^a-z]/g, '') === k.toLowerCase().replace(/[^a-z]/g, ''));
      if (entry) return String(entry[1]);
    }
    return '—';
  };

  const topBarFields = [
    { label: 'Patient', value: getField('PatientName', 'patientname', 'patient_name') },
    { label: 'DOB', value: getField('PatientDob', 'patientdob', 'dob') },
    { label: 'Carrier', value: getField('InsuranceCompanyName', 'carrier', 'payer', 'insurancecompany') },
    { label: 'Member ID', value: getField('SsnOrMemberId', 'memberid', 'member_id', 'memberId') },
  ];

  return (
    <div style={{ border: '1px solid #e0e0e0', borderRadius: 10, overflow: 'hidden', background: '#fff', boxShadow: '0 2px 12px rgba(0,0,0,0.08)' }}>

      {/* ── Top info bar ── */}
      <div style={{ background: '#f8f9fa', borderBottom: '1px solid #e0e0e0', padding: '14px 24px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
          <div style={{ display: 'flex', gap: 28, flexWrap: 'wrap' }}>
            {topBarFields.map(f => (
              <div key={f.label}>
                <div style={{ fontSize: 10, color: '#888', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.6 }}>{f.label}</div>
                <div style={{ fontSize: 13, fontWeight: 600, color: '#222', marginTop: 2 }}>{f.value}</div>
              </div>
            ))}
          </div>
          <div style={{ display: 'flex', gap: 16, alignItems: 'center', flexShrink: 0 }}>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontWeight: 700, fontSize: 13, color: '#222' }}>Total Fields</div>
              <div style={{ fontSize: 12, color: '#555' }}>
                {Object.entries(editableData).filter(([k]) => k && k !== '').length} fields across {sections.length} sections
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
            {isEditMode ? (
              <>
                <Button color="danger" size="sm" onClick={handleCancel} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  <XCircle size={14} /> Cancel
                </Button>
                <Button color="success" size="sm" onClick={handleSave} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  Save
                </Button>
              </>
            ) : (
              <Button color="primary" size="sm" onClick={() => setIsEditMode(true)} style={{ display: 'flex', alignItems: 'center', gap: 6, backgroundColor: '#1abc9c', border: 'none' }}>
                Edit <Edit size={13} />
              </Button>
            )}
            </div>
          </div>
        </div>
      </div>

      {/* ── Body ── */}
      <div style={{ display: 'flex', minHeight: '65vh', position: 'relative' }}>

        {/* Main content — all sections stacked */}
        <div style={{ flex: 1, padding: '20px 24px', overflowY: 'auto', maxHeight: '65vh' }}>
          {loading && (
            <div className="loading-overlay">
              <div id="page"><div id="container">
                <div id="ring"></div><div id="ring"></div><div id="ring"></div><div id="ring"></div>
                <div id="h3" style={{ color: 'black' }}>Loading...</div>
              </div></div>
            </div>
          )}
          {sections.map((section, idx) => (
            <div
              key={section}
              style={{ marginBottom: 32, paddingBottom: idx < sections.length - 1 ? 24 : 0, borderBottom: idx < sections.length - 1 ? '1px solid #eceff1' : 'none' }}
            >
              <h6 style={{ fontWeight: 700, marginBottom: 20, color: '#222', fontSize: 16 }}>{section}</h6>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '20px 20px' }}>
                {(sectionedData[section] || []).map(([key, value]) => (
                  <div key={key} style={{ display: 'flex', flexDirection: 'column' }}>
                    <label style={{ fontSize: 10, fontWeight: 700, color: '#999', textTransform: 'uppercase', letterSpacing: 0.6, marginBottom: 5 }}>
                      {formatLabel(key)}
                    </label>
                    <input
                      type="text"
                      value={String(value)}
                      onChange={e => handleChange(key, e.target.value)}
                      disabled={!isEditMode}
                      style={{
                        background: isEditMode ? '#e6f7fa' : '#f5f8fa',
                        border: isEditMode ? '1px solid #7ecbcb' : '1px solid #e8e8e8',
                        borderRadius: 6, padding: '9px 12px', fontSize: 14, color: '#333',
                        cursor: isEditMode ? 'text' : 'default', outline: 'none',
                      }}
                    />
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

      </div>
    </div>
  );
};


const PatientDetails = () => {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const view: any = queryParams.get('view');
  const [data, setData] = useState<any[]>([]);
  const [TemplateData, setTemplateData] = useState<TemplateItem[]>([]);
  const [expandAll, setExpandAll] = useState(true); // State for button label
  const navigate = useNavigate();
  const [permission, setPermission] = useState<{ id: string; screenshot_status: string; patient_name: string }[]>([]);
  const [activeTabhtml, setActiveTabhtml] = useState<"patient" | "code">("patient");
  const [patientRawFiles, setPatientRawFiles] = useState<RawDataFileEntry[]>([]);
  const [codeRawFiles, setCodeRawFiles] = useState<RawDataFileEntry[]>([]);
  const [isLoadingRawData, setIsLoadingRawData] = useState(false);
  // stores measured heights: key is iframe id (string) -> height number (px)
  const [iframeHeights, setIframeHeights] = useState<Record<string, number>>({});
  const [showApiDetails, setShowApiDetails] = useState(false);
  const [isAllOpen, setIsAllOpen] = useState(true);
  const [decryptedTemplateData, setDecryptedTemplateData] = useState<any[]>([]);
  // Published Form Designer layout for this patient's template (null = use the field-grid view)
  const [designerLayout, setDesignerLayout] = useState<any | null>(null);
  const [layoutChecked, setLayoutChecked] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const key = await keyPromise;
        const EncParams = await AesEncrypt(key, { id: id! });
        const response = await api.fetch('api/Patient/GetPatientTemplateLayout', {
          method: 'POST',
          body: JSON.stringify({ EncParams }),
        });
        const result = await response.json();
        if (!cancelled) setDesignerLayout(result?.isDesigner ? result.layoutSchema : null);
      } catch (error) {
        console.error('Error fetching template layout:', error);
        if (!cancelled) setDesignerLayout(null);
      } finally {
        if (!cancelled) setLayoutChecked(true);
      }
    })();
    return () => { cancelled = true; };
  }, [id]);

  // To check the Screenshot permission
  const screenshotPermission = useCallback(async () => {
    try {
      const key = await keyPromise
      const EncParams = await AesEncrypt(key, { id: id! });
      const response = await api.fetch('api/screenshot-Permission', {
        method: 'POST',
        body: JSON.stringify({ EncParams })
      });
      const result = await response.json();
      setPermission(result)
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  }, [id]);

  const fetchData = useCallback(async () => {
    try {
      const key = await keyPromise
      const EncParams = await AesEncrypt(key, { id: id! });
      const response = await api.fetch('api/Patient/GetViewPatientData', {
        method: 'POST',
        body: JSON.stringify({ EncParams })
      });
      const result = await response.json();
      // console.log("Patient Eligibility Data Response:", result); // Debug log to check the response structure
      if (result === "There is no Record Found") {
        setData([]);
        setTemplateData([]);
      } else {
        setData(result.normal);
        setTemplateData(result.Template);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  }, [id]);

  type RawDataFileKind = "html" | "md";
  type RawDataFileEntry = { path: string; kind: RawDataFileKind; content: string };

  // add listener for postMessage from iframes
  useEffect(() => {
    // Strongly-typed listener for iframe postMessage heights
    type IframeMsg = { type: "iframeHeight"; id: string; height: number };
    const onMessage = (e: MessageEvent) => {
      const raw = e.data;

      // Basic runtime checks: must be an object
      if (!raw || typeof raw !== "object") return;

      // Narrow the shape with runtime checks before using
      const possible = raw as Partial<IframeMsg>;
      if (possible.type !== "iframeHeight") return;
      if (typeof possible.id !== "string") return;
      if (typeof possible.height !== "number") return;

      // Now TypeScript knows possible.id is string and possible.height is number
      const data: IframeMsg = {
        type: "iframeHeight",
        id: possible.id,
        height: possible.height,
      };

      setIframeHeights((prev) => {
        // prev is Record<string, number>
        if (prev[data.id] === data.height) return prev;
        return { ...prev, [data.id]: data.height };
      });
    };

    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, []);

  // Inject small script into srcDoc to send height to parent
  const injectAutoResizeScript = (html: string, iframeId: string) => {
    const script = `<script>(function(){function sendHeight(){try{var h = Math.max(document.documentElement.scrollHeight || 0, document.body.scrollHeight || 0); parent.postMessage({type:'iframeHeight', id:'${iframeId}', height: h}, '*');}catch(e){} } window.addEventListener('load', sendHeight); window.addEventListener('resize', sendHeight); var mo = new MutationObserver(sendHeight); mo.observe(document.documentElement || document.body, {childList:true, subtree:true, attributes:true}); setTimeout(sendHeight, 120);})();</script>`;

    if (/<\/body>/i.test(html)) {
      return html.replace(/<\/body>/i, script + "</body>");
    }
    // no body tag -> append script at end
    return html + script;
  };

  const handlerawdata = useCallback(async (): Promise<void> => {
    setIsLoadingRawData(true);
    setPatientRawFiles([]);
    setCodeRawFiles([]);
    setIframeHeights({});

    try {
      const res = await api.fetch("api/rawdata", {
        method: "POST",
        body: JSON.stringify({ id })
      });

      if (!res.ok) {
        const err = await res.json().catch(() => null);
        console.error("Backend error", err);
        setIsLoadingRawData(false);
        return;
      }

      const blob = await res.blob();
      const zip = await JSZip.loadAsync(blob);
      const allFiles = Object.keys(zip.files);
      // console.log("All files in zip:", allFiles); // Debug log to check the files in the zip
      const isSupportedRawFile = (f: string) => f.endsWith(".html") || f.endsWith(".md");
      const getKindFromPath = (f: string): RawDataFileKind => (f.endsWith(".md") ? "md" : "html");

      const patientFileNames = allFiles
        .filter((f) => (f.includes("Patient_details/") || f.includes("Html_Extracted/")) && isSupportedRawFile(f))
        .sort((a, b) => {
          const aNum = parseInt(a.match(/step_(\d+)_/)?.[1] || "0");
          const bNum = parseInt(b.match(/step_(\d+)_/)?.[1] || "0");
          return aNum - bNum;
        });

      const codeFileNames = allFiles
        .filter((f) => f.includes("code_details/") && isSupportedRawFile(f))
        .sort((a, b) => {
          const aNum = parseInt(a.match(/step_(\d+)_/)?.[1] || "0");
          const bNum = parseInt(b.match(/step_(\d+)_/)?.[1] || "0");
          return aNum - bNum;
        });

      // read files in batch
      const patientEntries: RawDataFileEntry[] = [];
      for (const f of patientFileNames) {
        const fileObj = zip.file(f);
        if (!fileObj) continue;
        const txt = (await fileObj.async("string")) as string | undefined;
        if (txt && txt.trim()) {
          patientEntries.push({ path: f, kind: getKindFromPath(f), content: txt });
        }
      }

      const codeEntries: RawDataFileEntry[] = [];
      for (const f of codeFileNames) {
        const fileObj = zip.file(f);
        if (!fileObj) continue;
        const txt = (await fileObj.async("string")) as string | undefined;
        if (txt && txt.trim()) {
          codeEntries.push({ path: f, kind: getKindFromPath(f), content: txt });
        }
      }
      setPatientRawFiles(patientEntries);
      setCodeRawFiles(codeEntries);

      if (patientEntries.length > 0) setActiveTabhtml("patient");
      else if (codeEntries.length > 0) setActiveTabhtml("code");
    } catch (err) {
      console.error("Error fetching/unzipping:", err);
    } finally {
      setIsLoadingRawData(false);
    }
  }, [id, webservice]);

  useEffect(() => {
    fetchData();
    screenshotPermission();
    handlerawdata();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchData, screenshotPermission, handlerawdata]);

  // render iframe with injected script and dynamic height
  const renderIframe = (rawHtml: string, iframeId: string) => {
    const srcDoc = injectAutoResizeScript(rawHtml, iframeId);
    const height = iframeHeights[iframeId] ?? 200; // fallback min height

    return (
      <iframe
        id={iframeId}
        key={iframeId}
        srcDoc={srcDoc}
        title={iframeId}
        sandbox="allow-same-origin allow-scripts"
        style={{
          width: "100%",
          height: `${height}px`,
          border: "none",
          display: "block",
          background: "#fff",
        }}
      />
    );
  };

  const renderMarkdown = (md: string, key: string) => {
    return (
      <pre
        key={key}
        style={{
          whiteSpace: "pre-wrap",
          wordBreak: "break-word",
          margin: 0,
          padding: "12px",
          border: "1px solid #e5e7eb",
          borderRadius: "8px",
          background: "#fff",
          fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace",
          fontSize: "13px",
          lineHeight: 1.45,
        }}
      >
        {md}
      </pre>
    );
  };

  useEffect(() => {
    fetchData();
    screenshotPermission();
    handlerawdata();
  }, [fetchData, screenshotPermission, handlerawdata]);

  if (permission.length > 0) {
    localStorage.setItem("patient_name", permission[0].patient_name);
  }

  const toggleAllSections = () => {
    setIsAllOpen(prevState => !prevState);
  };

  const JsonViewer: React.FC<{ data: any }> = ({ data }) => {
    // State to track if all sections are open
    if (typeof data !== 'object' || data === null) {
      return <span>{String(data)}</span>;
    }

    return (
      <div className="json-viewer">
        {Object.entries(data).map(([key, value], index) => (
          <JsonNode key={index} nodeKey={key} value={value} isOpen={isAllOpen} />
        ))}
      </div>
    );
  };

  const JsonNode: React.FC<{ nodeKey: string; value: any; isOpen: boolean }> = ({ nodeKey, value, isOpen }) => {
    const [isLocalOpen, setIsLocalOpen] = useState(isOpen); // Local state for individual node
    const isObject = typeof value === 'object' && value !== null;
    const toggleLocalNode = () => {
      setIsLocalOpen(prev => !prev);
    };

    React.useEffect(() => {
      setIsLocalOpen(isOpen);
    }, [isOpen]);

    return (
      <div className="json-node">
        <div className="json-card">
          <div
            className="json-card-header"
            onClick={toggleLocalNode} // Toggle individual node
          >
            <span className="json-card-title">
              {nodeKey.toUpperCase()}:
            </span>
            {isObject && (
              <span style={{ marginLeft: '8px', cursor: 'pointer' }}>
                {isLocalOpen ? '−' : '+'} {/* Plus/Minus Icon */}
              </span>
            )}
          </div>
          {isLocalOpen && isObject && (  // Use local state to control visibility
            <div className="json-card-body">
              <JsonViewer data={value} />
            </div>
          )}
          {!isObject && <span className="json-value">{String(value)}</span>}
        </div>
      </div>
    );
  };

  const handleback = async () => {
    if (view === 'ManualPatientView') {
      navigate(`/BulkUpload?view=manual`);
    } else if (view === 'PatientsView') {
      navigate(`/patients`);
    } else {
      navigate(view);
    }
  };

  const UserId = localStorage.id;
  useEffect(() => {
    const fetchData = async () => {
      const key = await keyPromise
      const EncParams = await AesEncrypt(key, UserId);
      try {
        const response = await api.fetch("api/TemplateCreation/get_patient_details", {
          method: 'POST',
          body: JSON.stringify({ EncParams }),
        });
        if (!response.ok) {
          throw new Error('Failed to fetch data');
        }
        const result = await response.json();
        const decryptedResult = await AesDecrypt(key, result);

        if (decryptedResult.message === 'There is no Record Found') {
          setDecryptedTemplateData([]);
        } else {
          setDecryptedTemplateData(Object.entries(decryptedResult[0]));
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };
    // Initial fetch of patient data
    fetchData();
    // WebSocket setup
    const socket = new WebSocket(`${websocket_webservice}ws/Tempalatedata/`);
    socket.onmessage = () => {
      fetchData();
    };
    // Clean up the WebSocket connection when the component unmounts
    return () => {
      if (socket.readyState === 1) {
        socket.close();
      }
    }
  }, []);


  return (
    <>
      <Breadcrumbs mainTitle='Patient Eligibility Info' parent="Patient" title="Patient Eligibility Info" to={`/patients`} />
      <Container fluid>
        {/* Header row */}
        <Row className="align-items-center mb-3">
          <Col xs="auto">
            <Button color="primary" onClick={handleback}>
              Back
            </Button>
          </Col>
          <Col className="d-flex justify-content-end align-items-center" style={{ gap: '10px' }}>
            <Button
              color="info"
              onClick={() => setShowApiDetails(prev => !prev)}
            >
              {showApiDetails ? 'Hide Portal Raw Details' : 'Portal Raw Details'}
            </Button>
            {permission.length > 0 && permission[0].screenshot_status === 'Yes' && (
              <Button color="primary" onClick={() => { navigate(`/Patientimage/${id}`); }}>
                Portal Screenshot
              </Button>
            )}
          </Col>
        </Row>

        {/* API Details panel — slides in when button clicked */}
        {showApiDetails && (
          <Row className="mb-3">
            <Col sm="12">
              <Card>
                <CardHeader>
                  <div className="d-flex justify-content-between align-items-center">
                    <CardTitle tag="h5" className="mb-0">Raw HTML Output</CardTitle>
                    <Nav tabs style={{ backgroundColor: "#f8f9fa" }}>
                      <NavItem>
                        <NavLink
                          className={activeTabhtml === "patient" ? "active" : ""}
                          onClick={() => setActiveTabhtml("patient")}
                          style={{ cursor: "pointer" }}
                        >
                          Patient Details
                        </NavLink>
                      </NavItem>
                      <NavItem>
                        <NavLink
                          className={activeTabhtml === "code" ? "active" : ""}
                          onClick={() => setActiveTabhtml("code")}
                          style={{ cursor: "pointer" }}
                        >
                          Code Details
                        </NavLink>
                      </NavItem>
                    </Nav>
                  </div>
                </CardHeader>
                <CardBody>
                  <div style={{ maxHeight: "60vh", overflowY: "auto", padding: "10px" }}>
                    {isLoadingRawData ? (
                      <div className="d-flex justify-content-center align-items-center" style={{ height: "200px" }}>
                        <p>Loading...</p>
                      </div>
                    ) : (
                      <TabContent activeTab={activeTabhtml} style={{ marginTop: "15px" }}>
                        <TabPane tabId="patient">
                          {patientRawFiles.length === 0 ? (
                            <p>No Patient Details found.</p>
                          ) : (
                            patientRawFiles.map((file, index) => {
                              const iframeId = `patient-${index}`;
                              return (
                                <div key={iframeId} style={{ marginBottom: "20px" }}>
                                  {file.kind === "html"
                                    ? renderIframe(file.content, iframeId)
                                    : renderMarkdown(file.content, iframeId)}
                                </div>
                              );
                            })
                          )}
                        </TabPane>
                        <TabPane tabId="code">
                          {codeRawFiles.length === 0 ? (
                            <p>No Code Details found.</p>
                          ) : (
                            codeRawFiles.map((file, index) => {
                              const iframeId = `code-${index}`;
                              return (
                                <div key={iframeId} style={{ marginBottom: "20px" }}>
                                  {file.kind === "html"
                                    ? renderIframe(file.content, iframeId)
                                    : renderMarkdown(file.content, iframeId)}
                                </div>
                              );
                            })
                          )}
                        </TabPane>
                      </TabContent>
                    )}
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>
        )}

        {/* Patient Details (Template Information) — shown by default always */}
        <Row>
          <Col sm="12">
            {!layoutChecked ? null : designerLayout ? (
              <DesignerTemplateView
                layout={designerLayout}
                data={(TemplateData as any) || {}}
                id={id}
              />
            ) : (
              <Template
                template_data_get_heading={TemplateData}
                id={id}
                isOpen={true}
                toggle={() => { }}
                decryptedTemplateData={decryptedTemplateData}
              />
            )}
          </Col>
        </Row>
      </Container>
    </>
  );
};

export default PatientDetails;
